import ApiClient from '@/services/apiClient/ApiClient'
import type {
  Tag,
  GroupLandTagWithDetails,
  CreateUserGroupLandTagDto,
} from '@popety-io/types'

const apiClient = new ApiClient()

export const groupLandTagsService = {
  /**
   * Get all group land tags (for map coloring and filtering)
   */
  getAllGroupLandTags: async (): Promise<GroupLandTagWithDetails[]> => {
    try {
      const tagsResponse = await apiClient.get('/tags/group-land/all')
      if (tagsResponse.errors) {
        throw new Error(tagsResponse.errors[0].message)
      }
      return tagsResponse.data as GroupLandTagWithDetails[]
    } catch (error: any) {
      console.error('Failed to get all group land tags:', error)
      throw new Error(error.message || 'Failed to fetch group land tags')
    }
  },

  /**
   * Get tags for a specific group land
   */
  getTagsForGroupLand: async (
    groupLandId: string
  ): Promise<GroupLandTagWithDetails[]> => {
    try {
      const response = await apiClient.get(`/tags/group-land/${groupLandId}`)
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as GroupLandTagWithDetails[]
    } catch (error: any) {
      console.error(`Failed to get tags for group land ${groupLandId}:`, error)
      throw new Error(error.message || 'Failed to fetch tags for group land')
    }
  },

  /**
   * Add a tag to a group land (WITH CASCADE LOGIC)
   * This will automatically create the same tag for all lands in the group
   */
  addTagToGroupLand: async (
    groupLandId: string,
    tagId: string,
    primary: boolean = false
  ): Promise<GroupLandTagWithDetails> => {
    try {
      const data: CreateUserGroupLandTagDto = {
        land_grouping_id: groupLandId,
        tag_id: tagId,
        primary,
      }

      const response = await apiClient.post('/tags/group-land', data)
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as GroupLandTagWithDetails
    } catch (error: any) {
      console.error(`Failed to add tag to group land ${groupLandId}:`, error)
      throw new Error(error.message || 'Failed to add tag to group land')
    }
  },

  /**
   * Remove a tag from a group land (WITH CASCADE LOGIC)
   * This will automatically remove the same tag from all lands in the group
   */
  removeTagFromGroupLand: async (groupLandTagId: string): Promise<void> => {
    try {
      const response = await apiClient.delete(
        `/tags/group-land/${groupLandTagId}`
      )
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
    } catch (error: any) {
      console.error(`Failed to remove group land tag ${groupLandTagId}:`, error)
      throw new Error(error.message || 'Failed to remove tag from group land')
    }
  },

  /**
   * Get all available tags (shared with lands and buildings)
   */
  getAllTags: async (): Promise<Tag[]> => {
    try {
      const response = await apiClient.get('/tags/all')
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as Tag[]
    } catch (error: any) {
      console.error('Failed to get all tags:', error)
      throw new Error(error.message || 'Failed to fetch all tags')
    }
  },

  /**
   * Create a new tag (can be used for group lands, lands, or buildings)
   */
  createTag: async (value: string, color: string): Promise<Tag> => {
    try {
      const response = await apiClient.post('/tags', {
        value: value.trim(),
        color,
      })
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as Tag
    } catch (error: any) {
      console.error('Failed to create tag:', error)
      throw new Error(error.message || 'Failed to create tag')
    }
  },
}
